
import React, { useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { 
  Form,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import ProgramBasicInfoSection, { programBasicInfoSchema } from "./ProgramBasicInfoSection";
import HealthInformationSection, { healthInfoSchema } from "./HealthInformationSection";

// Combine schemas from each section
const programFormSchema = z.object({
  ...programBasicInfoSchema.shape,
  ...healthInfoSchema.shape,
}).refine((data) => {
  // If medicalConditions is "yes", then medicalDetails must be provided
  if (data.medicalConditions === "yes") {
    return !!data.medicalDetails?.trim();
  }
  return true;
}, {
  message: "Please provide details about the medical conditions",
  path: ["medicalDetails"],
});

type ProgramFormValues = z.infer<typeof programFormSchema>;

type ProgramFormProps = {
  onBack: () => void;
  onContinue: () => void;
};

export default function ProgramForm({
  onBack,
  onContinue
}: ProgramFormProps) {
  // Load saved form data from localStorage when component mounts
  const savedFormData = localStorage.getItem('programData');
  const parsedFormData = savedFormData ? JSON.parse(savedFormData) : null;

  const programForm = useForm<ProgramFormValues>({
    resolver: zodResolver(programFormSchema),
    defaultValues: parsedFormData || {
      tier: "Beginner",
      programName: "",
      description: "",
      duration: "4",
      daysPerWeek: "3",
      medicalConditions: "no",
      medicalDetails: "",
      injuries: "",
    },
    mode: "onChange", // Validate fields on change for better user experience
  });
  
  const { formState: { errors, isValid, isDirty } } = programForm;

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    const subscription = programForm.watch((data) => {
      if (Object.keys(data).length > 0) {
        localStorage.setItem('programData', JSON.stringify(data));
      }
    });
    
    return () => subscription.unsubscribe();
  }, [programForm]);
  
  const handleGoToTierQuestions = () => {
    const formValues = programForm.getValues();
    const isFormValid = programForm.trigger();
    
    isFormValid.then(valid => {
      if (valid) {
        // Save program data to local storage before proceeding to tier questions
        localStorage.setItem('programData', JSON.stringify(formValues));
        onContinue();
      } else {
        toast({
          title: "Missing required fields",
          description: "Please fill in all required fields before continuing.",
          variant: "destructive"
        });
      }
    });
  };

  return (
    <Card className="backdrop-blur-lg bg-white/10 border-white/20">
      <CardHeader>
        <CardTitle className="text-white">Program Details</CardTitle>
        <CardDescription className="text-white/70">
          Create a new training program
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...programForm}>
          <form className="space-y-4" onSubmit={e => e.preventDefault()}>
            {/* Basic Program Information */}
            <ProgramBasicInfoSection form={programForm} />
            
            {/* Health & Safety Information */}
            <HealthInformationSection form={programForm} />
          </form>
        </Form>
        
        {Object.keys(errors).length > 0 && (
          <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-md">
            <p className="text-red-300 font-medium">Please fix the following errors:</p>
            <ul className="list-disc pl-5 mt-1 text-red-200 text-sm">
              {Object.entries(errors).map(([key, error]) => (
                <li key={key}>{error?.message?.toString()}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          className="bg-white/10 text-white hover:bg-white/20 border-white/30"
          onClick={onBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Client
        </Button>
        
        <Button variant="purple" onClick={handleGoToTierQuestions}>
          Continue to Questions
        </Button>
      </CardFooter>
    </Card>
  );
}
